import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button, Stack } from '@mui/material';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AttachmentResponse } from '@/api/data-contracts';
import useVersions from '@/features/media/ui/FileManager/useVersions';
import { Title } from '@/features/media/ui/FileVersions/FileVersions.styles';
import Version from '@/features/media/ui/FileVersions/Version';
import { queryKeys } from '@/react-query/constants';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

interface Props {
  attachment: AttachmentResponse;
  activeFileIndex: number;
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
  setActiveFile: Dispatch<SetStateAction<File | undefined>>;
  mediaPlanId?: string;
  orderId?: string;
  bookingId?: string;
  advertisingSpaceId?: string;
  disableVersionUpload?: boolean;
  category?: 'keyVisual' | 'artWork';
}

const FileVersions: FC<Props> = ({
  attachment: attachmentProp,
  activeFileIndex,
  files,
  setFiles,
  setActiveFile,
  mediaPlanId,
  orderId,
  bookingId,
  advertisingSpaceId,
  disableVersionUpload,
  category,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    downloadAttachments,
    uploadVersion: uploadVersionRequest,
    setActiveVersion: setActiveVersionRequest,
    deleteVersion: deleteVersionRequest,
  } = useVersions();
  const { enqueueSnackbar } = useSnackbar();

  const [versionFiles, setVersionFiles] = useState<File[]>([]);
  const [attachment, setAttachment] = useState<AttachmentResponse>(attachmentProp);

  useEffect(() => {
    if (attachment.versions.length) {
      downloadAttachments(
        attachment.versions.map((v) => v.filePointer),
        {
          orderId,
          bookingId,
          mediaPlanId,
          advertisingSpaceId,
        },
      ).then((response) => {
        setVersionFiles(response);
      });
    }
  }, [
    advertisingSpaceId,
    attachment.versions,
    bookingId,
    downloadAttachments,
    mediaPlanId,
    orderId,
  ]);

  useEffect(() => {
    if (versionFiles.length && versionFiles.length === attachment.versions.length) {
      const activeVersionIndex = attachment.versions.findIndex(
        (v) => v.id === attachment.activeVersion,
      );

      if (
        activeVersionIndex > -1 &&
        files[activeFileIndex]?.name !== versionFiles[activeVersionIndex]?.name
      ) {
        setFiles((prev) => {
          const newFiles = [...prev];
          newFiles[activeFileIndex] = versionFiles[activeVersionIndex];
          return newFiles;
        });
      }
    }
  }, [
    activeFileIndex,
    attachment.activeVersion,
    attachment.versions,
    files,
    setFiles,
    versionFiles,
  ]);

  const { mutate: uploadVersion } = useMutation(
    (files: File[]) =>
      uploadVersionRequest({
        attachmentId: attachment.id,
        mediaPlanId,
        orderId,
        category: category ?? 'artWork',
        keyVisuals: category === 'keyVisual' ? files : [],
        artWork: category !== 'keyVisual' ? files : [],
      }),
    {
      onSuccess: async (response: AttachmentResponse | undefined) => {
        if (response) {
          setAttachment((prev) => {
            return {
              ...prev,
              versions: response.versions,
              activeVersion: response.activeVersion,
            };
          });

          await queryClient.invalidateQueries([queryKeys.orders]);
          await queryClient.invalidateQueries([queryKeys.serviceOrders]);
          await queryClient.invalidateQueries([queryKeys.keyVisuals, 'status']);
        }
      },
    },
  );

  const { mutate: setActiveVersion } = useMutation(
    (versionId: number) =>
      setActiveVersionRequest({
        attachmentId: attachment.id,
        orderId,
        mediaPlanId,
        versionId,
      }),
    {
      onSuccess: async (response: AttachmentResponse | undefined) => {
        if (response) {
          setAttachment((prev) => ({
            ...prev,
            versions: response.versions,
            activeVersion: response.activeVersion,
          }));

          await queryClient.invalidateQueries([queryKeys.orders]);
          await queryClient.invalidateQueries([queryKeys.serviceOrders]);
          await queryClient.invalidateQueries([queryKeys.keyVisuals, 'status']);
        }
      },
    },
  );

  const { mutate: deleteVersion } = useMutation(
    (versionId: number) =>
      deleteVersionRequest({
        attachmentId: attachment.id,
        orderId,
        mediaPlanId,
        versionId,
      }),
    {
      onSuccess: async (response: AttachmentResponse | undefined) => {
        if (response) {
          setAttachment((prev) => ({
            ...prev,
            versions: response.versions,
          }));

          await queryClient.invalidateQueries([queryKeys.orders]);
          await queryClient.invalidateQueries([queryKeys.serviceOrders]);
          await queryClient.invalidateQueries([queryKeys.keyVisuals, 'status']);
        }
      },
    },
  );

  const getVersionNumber = useCallback(
    (id: number) => {
      const ids = attachment.versions.map((v) => v.id).sort();
      return ids.findIndex((i) => i === id) + 1;
    },
    [attachment.versions],
  );

  const {
    getRootProps,
    getInputProps,
    open: openPicker,
  } = useDropzone({
    noClick: true,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'video/mp4': [],
    },
    multiple: false,
    noDrag: true,
    onDrop: (files: File[]) => uploadVersion(files),
    onDropRejected: (fileRejections) =>
      enqueueSnackbar(fileRejections[0]?.errors?.[0]?.message || 'Could not add the photos', {
        variant: 'warning',
      }),
  });

  const renderActiveVersion = useCallback(() => {
    if (attachment.activeVersion && attachment.versions.length) {
      const key = attachment.versions.findIndex((v) => v.id === attachment.activeVersion);

      return (
        <Stack gap="0.5rem" sx={{ width: '100%' }}>
          <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
            <Title>{t('Current Version')}</Title>
            {!disableVersionUpload && (
              <Button size="small" variant="outlined" {...getRootProps()} onClick={openPicker}>
                <input {...getInputProps()} />
                {t('Add version')}
              </Button>
            )}
          </Stack>

          <Version
            file={versionFiles?.[key]}
            setActiveFile={setActiveFile}
            version={attachment.versions[key]}
            versionNumber={getVersionNumber(attachment.activeVersion)}
            isActiveVersion
            disableVersionUpload={disableVersionUpload}
          />
        </Stack>
      );
    } else {
      return null;
    }
  }, [
    attachment.activeVersion,
    attachment.versions,
    t,
    disableVersionUpload,
    getRootProps,
    openPicker,
    getInputProps,
    versionFiles,
    setActiveFile,
    getVersionNumber,
  ]);

  const renderPastVersions = useCallback(
    () =>
      attachment.versions.length > 0 ? (
        <Stack gap="0.5rem" sx={{ width: '100%' }}>
          <Title>{t('Past Version')}</Title>

          {attachment.versions.map((version, key) => {
            if (version.id !== attachment.activeVersion) {
              return (
                <Version
                  key={key}
                  file={versionFiles?.[key]}
                  setActiveFile={setActiveFile}
                  version={version}
                  versionNumber={getVersionNumber(version.id)}
                  setActiveVersion={(versionId: number) => setActiveVersion(versionId)}
                  deleteVersion={(versionId: number) => deleteVersion(versionId)}
                />
              );
            }

            return null;
          })}
        </Stack>
      ) : null,
    [
      attachment.activeVersion,
      attachment.versions,
      deleteVersion,
      versionFiles,
      getVersionNumber,
      setActiveFile,
      setActiveVersion,
      t,
    ],
  );

  return (
    <Stack gap="2rem">
      {renderActiveVersion()}
      {renderPastVersions()}
    </Stack>
  );
};

export default FileVersions;
